import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, ExternalLink, Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { WebhookDelivery } from '@/lib/api/webhooks';

type WebhookDeliveryLogVariant = 'table' | 'compact';

interface WebhookDeliveryLogProps {
  deliveries: WebhookDelivery[];
  isLoading?: boolean;
  isError?: boolean;
  variant?: WebhookDeliveryLogVariant;
  emptyLabel?: string;
}

const formatDateTime = (iso?: string | null) => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatStatusLabel = (status: string) =>
  status
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const statusClassName = (status: string) => {
  const normalized = status.toLowerCase();
  if (normalized === 'delivered') return 'bg-green-100 text-green-700 border-green-200';
  if (normalized === 'failed') return 'bg-red-100 text-red-700 border-red-200';
  if (normalized === 'skipped') return 'bg-gray-100 text-gray-700 border-gray-200';
  return 'bg-yellow-100 text-yellow-700 border-yellow-200';
};

const deliveryReason = (delivery: WebhookDelivery) => {
  if (delivery.status === 'skipped') return delivery.skip_reason || 'No matching filter';
  if (delivery.status === 'failed') return delivery.error_message || delivery.response_body_excerpt || 'Delivery failed';
  if (delivery.http_status_code) return `HTTP ${delivery.http_status_code}`;
  return delivery.matched_filter ? `Matched ${delivery.matched_filter}` : 'Delivered';
};

const webhookLabel = (delivery: WebhookDelivery) =>
  delivery.webhook_name || delivery.webhook_url_host || 'Deleted webhook';

export const WebhookDeliveryStatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <Badge variant="outline" className={`capitalize ${statusClassName(status)}`}>
    {formatStatusLabel(status)}
  </Badge>
);

const LoadingState = () => (
  <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading deliveries...
  </div>
);

const EmptyState: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
    {label}
  </div>
);

const ErrorState = () => (
  <div className="flex items-center justify-center py-8 text-sm text-destructive">
    Failed to load webhook deliveries.
  </div>
);

export const WebhookDeliveryLog: React.FC<WebhookDeliveryLogProps> = ({
  deliveries,
  isLoading,
  isError,
  variant = 'table',
  emptyLabel = 'No webhook deliveries recorded yet.',
}) => {
  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState />;
  if (deliveries.length === 0) return <EmptyState label={emptyLabel} />;

  if (variant === 'compact') {
    return (
      <div className="divide-y divide-border/70">
        {deliveries.map((delivery) => (
          <div key={delivery.id} className="py-2.5 first:pt-0 last:pb-0">
            <div className="flex items-center justify-between gap-2">
              <span className="min-w-0 truncate text-xs font-medium text-foreground">
                {webhookLabel(delivery)}
              </span>
              <WebhookDeliveryStatusBadge status={delivery.status} />
            </div>
            <div className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Clock className="h-3 w-3 shrink-0" />
              <span>{formatDateTime(delivery.created_at)}</span>
              {delivery.attempt_number > 0 && (
                <span>Attempt {delivery.attempt_number}/{delivery.max_attempts}</span>
              )}
            </div>
            <p className="mt-1 line-clamp-2 text-[11px] leading-relaxed text-muted-foreground">
              {deliveryReason(delivery)}
            </p>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Status</TableHead>
            <TableHead>Webhook</TableHead>
            <TableHead>Session</TableHead>
            <TableHead>Trigger</TableHead>
            <TableHead>Attempt</TableHead>
            <TableHead>HTTP</TableHead>
            <TableHead>Time</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {deliveries.map((delivery) => (
            <TableRow key={delivery.id}>
              <TableCell>
                <WebhookDeliveryStatusBadge status={delivery.status} />
              </TableCell>
              <TableCell>
                <div className="max-w-[220px]">
                  <div className="truncate text-sm font-medium">{webhookLabel(delivery)}</div>
                  {delivery.webhook_url_host && (
                    <div className="truncate font-mono text-xs text-muted-foreground">
                      {delivery.webhook_url_host}
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell>
                {delivery.session_recording_id ? (
                  <Link
                    to={`/analytics/sessions?sessionId=${delivery.session_recording_id}`}
                    className="inline-flex max-w-[220px] items-center gap-1 truncate text-sm text-foreground hover:underline"
                  >
                    <span className="truncate">{delivery.session_email || delivery.session_recording_id}</span>
                    <ExternalLink className="h-3 w-3 shrink-0" />
                  </Link>
                ) : (
                  <span className="text-sm text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {delivery.matched_filter || delivery.skip_reason || delivery.event_type}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {delivery.attempt_number > 0 ? `${delivery.attempt_number}/${delivery.max_attempts}` : '—'}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {delivery.http_status_code ?? '—'}
              </TableCell>
              <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                {formatDateTime(delivery.created_at)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
